/**
 * Procedural surface textures, generated once at load time — the game ships no
 * image assets, so every material has to make its own detail.
 *
 * Everything here is built on tileable value noise: the lattice wraps at a fixed
 * period, so the resulting maps repeat seamlessly no matter how many times a
 * ribbon or terrain plane tiles them. Colour maps are written straight in
 * display (sRGB) bytes; roughness and normal maps stay linear.
 *
 * Road UV convention (see track.ts): u runs ACROSS the road (0 = left edge,
 * 1 = right edge) and v runs ALONG it. That lets the asphalt bake in a real
 * racing line — two rubbered-in wheel tracks — at fixed lateral positions.
 */
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Tileable value noise
// ---------------------------------------------------------------------------

/** Integer hash -> [0,1). Math.imul keeps the mixing in 32-bit lanes. */
function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Positive modulo, so lattice lookups wrap instead of mirroring at 0. */
function wrap(v: number, period: number): number {
  return ((v % period) + period) % period;
}

/** Smooth value noise on a `period x period` lattice — tiles seamlessly. */
function valueNoise(x: number, y: number, period: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  // Smoothstep the interpolants so the lattice grid never shows through.
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const x0 = wrap(xi, period);
  const x1 = wrap(xi + 1, period);
  const y0 = wrap(yi, period);
  const y1 = wrap(yi + 1, period);
  const n00 = hash2(x0, y0, seed);
  const n10 = hash2(x1, y0, seed);
  const n01 = hash2(x0, y1, seed);
  const n11 = hash2(x1, y1, seed);
  return (n00 * (1 - u) + n10 * u) * (1 - v) + (n01 * (1 - u) + n11 * u) * v;
}

/** Fractal sum of tileable value noise. `u`/`v` are in [0,1). */
function fbm(u: number, v: number, basePeriod: number, octaves: number, seed: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let period = basePeriod;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(u * period, v * period, period, seed + o * 1013);
    norm += amp;
    amp *= 0.5;
    period *= 2;
  }
  return sum / norm;
}

/** Public: the same fBm the textures use, so terrain displacement matches. */
export function surfaceNoise(u: number, v: number, period: number, octaves: number, seed: number): number {
  return fbm(u, v, period, octaves, seed);
}

// ---------------------------------------------------------------------------
// Texture assembly helpers
// ---------------------------------------------------------------------------

/** Wrap raw RGBA bytes in a repeating, mipmapped DataTexture. */
function dataTexture(
  data: Uint8Array<ArrayBuffer>,
  size: number,
  colorSpace: THREE.ColorSpace,
): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 8;
  tex.colorSpace = colorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Derive a tangent-space normal map from a height field by central differences.
 * `strength` scales the slope — higher means a chunkier, more pitted surface.
 */
function normalFromHeight(height: Float32Array, size: number, strength: number): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  const at = (x: number, y: number): number => height[wrap(y, size) * size + wrap(x, size)];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      const len = Math.hypot(-dx, -dy, 1);
      const i = (y * size + x) * 4;
      data[i] = ((-dx / len) * 0.5 + 0.5) * 255;
      data[i + 1] = ((-dy / len) * 0.5 + 0.5) * 255;
      data[i + 2] = (1 / len) * 0.5 * 255 + 127.5;
      data[i + 3] = 255;
    }
  }
  return dataTexture(data, size, THREE.NoColorSpace);
}

/** A colour + normal + roughness set describing one physical surface. */
export interface SurfaceTextures {
  map: THREE.Texture;
  normalMap: THREE.Texture;
  roughnessMap: THREE.Texture;
}

/** Release every GPU texture in a surface set. */
export function disposeSurface(s: SurfaceTextures): void {
  s.map.dispose();
  s.normalMap.dispose();
  s.roughnessMap.dispose();
}

// ---------------------------------------------------------------------------
// Asphalt
// ---------------------------------------------------------------------------

/**
 * Race-track tarmac: coarse chippings, large paving patches, bright aggregate
 * specks, and two rubbered-in wheel tracks baked at the racing line. `u` across
 * the road, `v` along it (see the module header).
 */
export function createAsphaltTextures(baseColor: number, seed = 7): SurfaceTextures {
  const size = 512;
  const base = new THREE.Color(baseColor);
  const color = new Uint8Array(size * size * 4);
  const rough = new Uint8Array(size * size * 4);
  const height = new Float32Array(size * size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;

      const grain = fbm(u, v, 110, 3, seed); // individual chippings
      const patch = fbm(u, v, 5, 4, seed + 77); // laying / repair patches
      const speck = valueNoise(u * 190, v * 190, 190, seed + 31); // aggregate

      // Two darker, polished wheel tracks either side of centre.
      const line = Math.min(Math.abs(u - 0.31), Math.abs(u - 0.69));
      const rubber = Math.max(0, 1 - line / 0.11) ** 1.6;
      // Gritty, un-driven strip right at the kerbs.
      const edge = Math.max(0, (Math.abs(u - 0.5) - 0.40) / 0.10);

      let shade = 0.76 + grain * 0.40 + (patch - 0.5) * 0.20;
      shade *= 1 - rubber * 0.30; // rubber darkens the line
      shade += edge * 0.10; // dust and dropped stones at the edge
      if (speck > 0.94) shade += (speck - 0.94) * 4.5; // bright chip catching light

      const i = (y * size + x) * 4;
      color[i] = Math.min(255, base.r * 255 * shade);
      color[i + 1] = Math.min(255, base.g * 255 * shade);
      color[i + 2] = Math.min(255, base.b * 255 * shade);
      color[i + 3] = 255;

      // Rubber polishes the surface; loose grit at the edge dulls it.
      const r = Math.min(1, Math.max(0, 0.93 - rubber * 0.34 - grain * 0.10 + edge * 0.05));
      const rb = r * 255;
      rough[i] = rb;
      rough[i + 1] = rb;
      rough[i + 2] = rb;
      rough[i + 3] = 255;

      // Height field: chippings stand proud, the polished line is flatter.
      height[y * size + x] = grain * (1 - rubber * 0.7) + (speck > 0.94 ? 0.5 : 0);
    }
  }

  return {
    map: dataTexture(color, size, THREE.SRGBColorSpace),
    roughnessMap: dataTexture(rough, size, THREE.NoColorSpace),
    normalMap: normalFromHeight(height, size, 14),
  };
}

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

/**
 * Everything off the tarmac — grass, sand, snow. Two tones mottled together
 * with a coarse patch mask plus fine speckle, so a 1400-unit plane never reads
 * as a flat colour even from the chase camera.
 */
export function createTerrainTextures(
  baseColor: number,
  accentColor: number,
  seed = 21,
): SurfaceTextures {
  const size = 512;
  const base = new THREE.Color(baseColor);
  const accent = new THREE.Color(accentColor);
  const color = new Uint8Array(size * size * 4);
  const rough = new Uint8Array(size * size * 4);
  const height = new Float32Array(size * size);
  const mixed = new THREE.Color();

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;

      const patch = fbm(u, v, 4, 4, seed); // meadow-scale variation
      const tuft = fbm(u, v, 40, 3, seed + 91); // individual clumps
      const fine = valueNoise(u * 150, v * 150, 150, seed + 7);

      mixed.copy(base).lerp(accent, Math.min(1, Math.max(0, patch * 1.3 - 0.15)));
      const shade = 0.80 + tuft * 0.34 + (fine - 0.5) * 0.14;

      const i = (y * size + x) * 4;
      color[i] = Math.min(255, mixed.r * 255 * shade);
      color[i + 1] = Math.min(255, mixed.g * 255 * shade);
      color[i + 2] = Math.min(255, mixed.b * 255 * shade);
      color[i + 3] = 255;

      const rb = Math.min(255, (0.88 + tuft * 0.12) * 255);
      rough[i] = rb;
      rough[i + 1] = rb;
      rough[i + 2] = rb;
      rough[i + 3] = 255;

      height[y * size + x] = tuft * 0.7 + fine * 0.3;
    }
  }

  return {
    map: dataTexture(color, size, THREE.SRGBColorSpace),
    roughnessMap: dataTexture(rough, size, THREE.NoColorSpace),
    normalMap: normalFromHeight(height, size, 9),
  };
}

// ---------------------------------------------------------------------------
// Kerbs
// ---------------------------------------------------------------------------

/**
 * The classic alternating kerb stripe, with a soft bevel shade across the strip
 * so it catches the sun like moulded concrete. Repeat this ALONG the kerb
 * ribbon; one tile is one pair of stripes.
 */
export function createKerbTexture(colorA: number, colorB: number): THREE.Texture {
  const size = 64;
  const a = new THREE.Color(colorA);
  const b = new THREE.Color(colorB);
  const data = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y++) {
    // v runs along the kerb: split the tile into two stripes.
    const stripe = y < size / 2 ? a : b;
    for (let x = 0; x < size; x++) {
      const u = x / (size - 1);
      // Bevel: darker at the inner edge, brightest along the crown.
      const bevel = 0.72 + 0.38 * Math.sin(u * Math.PI);
      const grime = 0.94 + valueNoise(u * 24, (y / size) * 24, 24, 5) * 0.12;
      const shade = bevel * grime;
      const i = (y * size + x) * 4;
      data[i] = Math.min(255, stripe.r * 255 * shade);
      data[i + 1] = Math.min(255, stripe.g * 255 * shade);
      data[i + 2] = Math.min(255, stripe.b * 255 * shade);
      data[i + 3] = 255;
    }
  }

  return dataTexture(data, size, THREE.SRGBColorSpace);
}

// ---------------------------------------------------------------------------
// Start / finish line
// ---------------------------------------------------------------------------

/**
 * Painted chequered flag, `cells` squares to a tile, weathered by tyre rubber so
 * it reads as paint on tarmac rather than a clean UI checkerboard.
 */
export function createCheckerTexture(cells = 8): THREE.Texture {
  const size = 256;
  const step = size / cells;
  const data = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dark = (Math.floor(x / step) + Math.floor(y / step)) % 2 === 1;
      const wear = 0.80 + fbm(x / size, y / size, 26, 3, 61) * 0.34;
      const level = (dark ? 26 : 236) * wear;
      const i = (y * size + x) * 4;
      data[i] = level;
      data[i + 1] = level;
      data[i + 2] = level;
      data[i + 3] = 255;
    }
  }

  return dataTexture(data, size, THREE.SRGBColorSpace);
}

// ---------------------------------------------------------------------------
// Particle sprite
// ---------------------------------------------------------------------------

/**
 * Soft round puff used by every particle system (tyre smoke, dust, sparks).
 * Alpha falls off smoothly from the centre and is broken up by a little noise so
 * overlapping puffs read as billowing volume rather than stacked discs.
 */
export function createPuffTexture(): THREE.Texture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - c, y - c) / c;
      const falloff = Math.max(0, 1 - d) ** 1.9;
      const mottle = 0.72 + fbm(x / size, y / size, 7, 3, 3) * 0.56;
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.min(255, falloff * mottle * 255);
    }
  }

  return dataTexture(data, size, THREE.SRGBColorSpace);
}
